import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'

import settings from '../../../settings'
import { VoucherSteps } from '../../../constants'
import { tr, useDelayedExecute } from 'mmfintech-commons'
import {
  useBanksRequest,
  useCancelPaymentRequest,
  useEventTracker,
  useFilteredBanks,
  usePaymentRequest
} from 'mmfintech-checkout-commons'

import { ErrorDisplay } from 'mmfintech-portal-commons'
import { CustomPreloader } from '@components'
import { VoucherBanksStep } from './VoucherBanksStep'
import { VoucherOptionsStep } from './VoucherOptionsStep'

import type { SupportedBank } from 'mmfintech-checkout-commons/types'
import type { VoucherOption, VoucherProps, VoucherStep } from '../../../@types'

export const CheckoutVoucher = (props: VoucherProps) => {
  const { sessionId, fieldValues, countryCode, setCountryCode, handlePay } = props

  const { checkoutPayError, checkoutPayFetching, session, sessionError } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching, session, sessionError } }) => ({
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError
    }),
    shallowEqual
  )

  const [step, setStep] = useState<string>(null)
  const [error, setError] = useState(null)
  const [option, setOption] = useState<VoucherOption>('buy')
  const [triggered, setTriggered] = useState(false)
  const [eventLogged, setEventLogged] = useState(false)

  const history = useHistory()

  const { cancelPayment } = useCancelPaymentRequest()
  const { reset: resetPayment } = usePaymentRequest()
  const { banks, banksCount, banksError, resetBanks } = useBanksRequest()

  const filteredBanks = useFilteredBanks((bank: SupportedBank) => handleSelectBank(bank ? bank.bankChoiceId : ''))

  const { showCancelButton } = session || {}

  const { trackEvent } = useEventTracker()

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    return false
  }

  const handleSelectOption = (value: VoucherOption): void => {
    setError(null)
    setOption(value)
  }

  const handleContinueFromOptions = () => {
    if (!sessionError && !checkoutPayError && !checkoutPayFetching) {
      switch (option) {
        case 'buy':
          if (banksCount === 1) {
            handlePay()
          } else {
            setStep(VoucherSteps.BANKS)
          }
          break

        case 'redeem':
          if (settings.redirect) {
            window.location.href = settings.redirect
          } else {
            setError(tr('CHECKOUT.VOUCHER.OPTIONS.SERVICE_UNAVAILABLE', 'Service is temporary unavailable.'))
          }
          break

        default:
        // nothing
      }
    }
  }

  const handleSelectBank = (bankCode: string) => {
    fieldValues.setValue('bankChoiceId', bankCode)
    void trackEvent(sessionId, 'bank_selected', bankCode)
  }

  const handleCountryChanged = (value: string): void => {
    if (value !== countryCode) {
      resetBanks()
      filteredBanks.reset()
      setCountryCode(value)
    }
  }

  const handleBankToStep1 = () => {
    resetPayment()
    setStep(VoucherSteps.OPTIONS)
  }

  const handleSubmit = () => {
    if (fieldValues.getValue('bankChoiceId') && !checkoutPayFetching) {
      handlePay()
    }
  }

  useEffect(() => {
    return () => {
      setStep(null)
      setError(null)
      setOption('buy')
      setTriggered(false)
    }
  }, [])

  useEffect(() => {
    if (step == null) {
      if (Array.isArray(banks)) {
        let nextStep: VoucherStep = VoucherSteps.OPTIONS
        const find = banks.find(entry => !!entry.preselect)
        if (find) {
          if (banksCount === 1) {
            const { bankChoiceId } = find
            handleSelectBank(bankChoiceId)
          }
          nextStep = VoucherSteps.BANKS
        } else if (banksCount === 1) {
          const { bankChoiceId } = banks[0]
          handleSelectBank(bankChoiceId)
        }
        if (nextStep === VoucherSteps.BANKS) {
          void trackEvent(sessionId, 'buy_redeem_page_skipped')
        }
        setStep(nextStep)
      } else {
        setStep(VoucherSteps.OPTIONS)
      }
    }
    // eslint-disable-next-line
  }, [banks])

  useEffect(() => {
    if (!triggered && fieldValues.getValue('bankChoiceId') && !checkoutPayFetching && banksCount === 1) {
      setTriggered(true)
      handleSubmit()
    }
    // eslint-disable-next-line
  }, [fieldValues.getValue('bankChoiceId')])

  const delayedLog = useDelayedExecute({
    onExecute: () => {
      void trackEvent(sessionId, 'banks_scrolling', '', true)
    }
  })

  const handleScroll = () => {
    delayedLog.execute()
    if (window.innerHeight + window.scrollY >= document.body.scrollHeight) {
      void trackEvent(sessionId, 'banks_scrolled_to_bottom', '', true)
    }
  }

  const logBankSearchFocus = () => trackEvent(sessionId, 'bank_search_focus', null, true)

  const logBankSearchType = () => {
    if (!eventLogged) {
      void trackEvent(sessionId, 'bank_search_type', null, true)
      setEventLogged(true)
    }
  }

  const removeEventListener = () => {
    window.removeEventListener('scroll', handleScroll)
  }

  useEffect(() => {
    if (step === VoucherSteps.BANKS) {
      window.addEventListener('scroll', handleScroll)
      return () => {
        removeEventListener()
      }
    } else {
      removeEventListener()
    }
    // eslint-disable-next-line
  }, [step])

  useEffect(() => {
    if (checkoutPayError) {
      if (step == null) {
        setStep(VoucherSteps.OPTIONS)
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }, [checkoutPayError])

  if (step == null) {
    return <CustomPreloader />
  }

  return (
    <>
      {step === VoucherSteps.OPTIONS ? (
        (!session?.brand || session?.brand === 'JETON_BANK') && settings.brand === 'MIKADO' ? (
          <CustomPreloader />
        ) : (
          <VoucherOptionsStep
            error={error}
            option={option}
            setOption={handleSelectOption}
            onContinue={handleContinueFromOptions}
            sessionId={sessionId}
          />
        )
      ) : step === VoucherSteps.BANKS ? (
        <VoucherBanksStep
          filteredBanks={filteredBanks}
          countryCode={countryCode}
          setCountryCode={handleCountryChanged}
          fieldValues={fieldValues}
          handleBack={handleBankToStep1}
          handleContinue={handlePay}
          logBankSearchFocus={logBankSearchFocus}
          logBankSearchType={logBankSearchType}
        />
      ) : step === VoucherSteps.FIELDS ? null : (
        <ErrorDisplay error={[sessionError, checkoutPayError, banksError]} />
      )}

      {showCancelButton ? (
        <div className='buttons-container'>
          <button type='button' className='cancel-btn' onClick={handleCancelClick} data-test='cancel-button'>
            {tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          </button>
        </div>
      ) : null}
    </>
  )
}
